import React, { useEffect, useState, useRef } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import { Link } from "react-router-dom";
import TaskSkeleton from "./TaskSkeleton";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const tableStyles = `
    .due-summary-table { font-size: 0.78rem; }
    .due-summary-table thead th { font-size: 0.72rem; padding: 6px 5px; white-space: nowrap; font-weight: 600; }
    .due-summary-table tbody td { font-size: 0.75rem; padding: 5px 5px; white-space: nowrap; }
    .class-header td { background: #e3f2fd !important; font-weight: 700; font-size: 0.78rem !important; }
    .class-total td { background: #e8f5e9 !important; font-weight: 700; }
    .grand-total td { background: #ffecb3 !important; font-weight: 700; font-size: 0.8rem !important; }
`;

export default function DueSummary() {
    const { http, user } = AuthUser();
    const [head1Data, setHead1Data] = useState([]);
    const [head2Data, setHead2Data] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [stClass, setStClass] = useState([]);
    const [stDev, setStDev] = useState([]);
    const [stYear, setStYear] = useState([]);
    const [stAgent, setStAgent] = useState([]);
    const [fees, setFees] = useState([]);

    const [selectedClass, setSelectedClass] = useState([]);
    const [showClassDropdown, setShowClassDropdown] = useState(false);
    const classDropdownRef = useRef(null);
    const [selectedDev, setSelectedDev] = useState("");
    const [selectedStdType, setSelectedStdType] = useState("");
    const [selectedDevYear, setSelectedDevYear] = useState("");
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedFeesType, setSelectedFeesType] = useState([]);
    const [dueDate, setDueDate] = useState("");
    const [dueDateEnd, setDueDateEnd] = useState("");
    const [showFeesDropdown, setShowFeesDropdown] = useState(false);
    const feesDropdownRef = useRef(null);

    useEffect(() => {
        http.get(`/class?company_id=${user.company_id}`).then((res) => setStClass(res.data.data));
        http.get(`/devision`).then((res) => setStDev(res.data.data));
        http.get(`/year`).then((res) => setStYear(res.data.data));
        http.get(`/agent?company_id=${user.company_id}`).then((res) => setStAgent(res.data.data));
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (feesDropdownRef.current && !feesDropdownRef.current.contains(e.target)) setShowFeesDropdown(false);
            if (classDropdownRef.current && !classDropdownRef.current.contains(e.target)) setShowClassDropdown(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelectAllClasses = () => {
        setSelectedClass(selectedClass.length === stClass.length ? [] : stClass.map((c) => c.class_name));
    };
    const handleToggleClass = (name) => {
        setSelectedClass((prev) => prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name]);
    };

    const handleSelectChangeYear = (event) => {
        setSelectedDevYear(event.target.value);
        http.get(`/tution_feeslists?company_id=${user.company_id}&year=${event.target.value}`)
            .then((res) => setFees(res.data.data)).catch(() => {});
    };

    const handleSelectAllFeeTypes = () => {
        setSelectedFeesType(selectedFeesType.length === fees.length ? [] : fees.map((f) => String(f.id)));
    };

    const handleToggleFeeType = (id) => {
        setSelectedFeesType((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
    };

    const fetchData = () => {
        setIsLoading(true);
        const classParam = Array.isArray(selectedClass) ? selectedClass.join(",") : selectedClass;
        const endpoint = `/due_summary?class=${classParam}&dev=${selectedDev}&start_date=${dueDate}&end_date=${dueDateEnd}&type=${selectedFeesType.join(",")}&company_id=${user.company_id}&std_type=${selectedStdType}&agent=${selectedAgent}`;
        http.get(endpoint)
            .then((res) => {
                setIsLoading(false);
                setHead1Data(res.data.head1 || []);
                setHead2Data(res.data.head2 || []);
            })
            .catch(() => { setIsLoading(false); toast.error("Error fetching summary"); });
    };

    const handleClear = () => window.location.reload();
    const fmt = (v) => parseFloat(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const buildClassHierarchy = (data) => {
        const classes = {};
        data.forEach((row) => {
            if (!classes[row.class]) classes[row.class] = [];
            classes[row.class].push(row);
        });
        return classes;
    };

    const calcGrandTotal = (data) => data.reduce((acc, r) => ({
        master: acc.master + r.master_amount, paid: acc.paid + r.paid_amount, due: acc.due + r.due, count: acc.count + r.student_count
    }), { master: 0, paid: 0, due: 0, count: 0 });

    const hasData = head1Data.length > 0 || head2Data.length > 0;

    // Render a grouped table for one head type
    const renderHeadTable = (data, title, colorClass) => {
        if (data.length === 0) return null;
        const hierarchy = buildClassHierarchy(data);
        const grand = calcGrandTotal(data);

        return (
            <div className="mb-4">
                <h6 className="fw-bold mb-2" style={{ color: colorClass }}><i className="fas fa-layer-group me-1"></i>{title} <span className="badge" style={{ backgroundColor: colorClass }}>{data.length} rows</span></h6>
                <div className="table-responsive">
                    <table className="table table-bordered due-summary-table">
                        <thead className="table-primary">
                            <tr>
                                <th style={{ width: "150px" }}>Class</th>
                                <th>{title}</th>
                                <th className="text-end" style={{ width: "90px" }}>Lines</th>
                                <th className="text-end" style={{ width: "130px" }}>Master Amount</th>
                                <th className="text-end" style={{ width: "130px" }}>Paid Amount</th>
                                <th className="text-end" style={{ width: "130px" }}>Due</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(hierarchy).sort().map((cls) => {
                                const rows = hierarchy[cls];
                                const classTotal = rows.reduce((a, r) => ({
                                    master: a.master + r.master_amount, paid: a.paid + r.paid_amount, due: a.due + r.due, count: a.count + r.student_count
                                }), { master: 0, paid: 0, due: 0, count: 0 });

                                return (
                                    <React.Fragment key={cls}>
                                        <tr className="class-header">
                                            <td colSpan={6}><strong>{cls}</strong></td>
                                        </tr>
                                        {rows.map((r, idx) => (
                                            <tr key={`${cls}-${idx}`}>
                                                <td></td>
                                                <td>{r.head}</td>
                                                <td className="text-end">{r.student_count}</td>
                                                <td className="text-end">{fmt(r.master_amount)}</td>
                                                <td className="text-end">{fmt(r.paid_amount)}</td>
                                                <td className="text-end">{fmt(r.due)}</td>
                                            </tr>
                                        ))}
                                        <tr className="class-total">
                                            <td><strong>{cls} Total</strong></td>
                                            <td></td>
                                            <td className="text-end"><strong>{classTotal.count}</strong></td>
                                            <td className="text-end"><strong>{fmt(classTotal.master)}</strong></td>
                                            <td className="text-end"><strong>{fmt(classTotal.paid)}</strong></td>
                                            <td className="text-end"><strong>{fmt(classTotal.due)}</strong></td>
                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                            <tr className="grand-total">
                                <td><strong>Grand Total</strong></td>
                                <td></td>
                                <td className="text-end"><strong>{grand.count}</strong></td>
                                <td className="text-end"><strong>{fmt(grand.master)}</strong></td>
                                <td className="text-end"><strong>{fmt(grand.paid)}</strong></td>
                                <td className="text-end"><strong>{fmt(grand.due)}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Excel: build one sheet per head type
    const buildExcelSheet = (data, title) => {
        const headerStyle = { font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 }, fill: { fgColor: { rgb: "1565C0" } }, alignment: { horizontal: "center" }, border: { bottom: { style: "thin", color: { rgb: "000000" } } } };
        const classStyle = { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: "BBDEFB" } } };
        const classTotalStyle = { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: "E8F5E9" } } };
        const grandTotalStyle = { font: { bold: true, sz: 11 }, fill: { fgColor: { rgb: "FFECB3" } } };
        const numFmt = { numFmt: "#,##0" };

        const headers = ["Class", title, "Lines", "Master Amount", "Paid Amount", "Due"];
        const hierarchy = buildClassHierarchy(data);
        const grand = calcGrandTotal(data);
        const rows = [];

        Object.keys(hierarchy).sort().forEach((cls) => {
            rows.push({ data: [cls, "", "", "", "", ""], style: classStyle });
            const clsRows = hierarchy[cls];
            let cm = 0, cp = 0, cd = 0, cc = 0;
            clsRows.forEach((r) => {
                rows.push({ data: ["", r.head, r.student_count, r.master_amount, r.paid_amount, r.due], style: null });
                cm += r.master_amount; cp += r.paid_amount; cd += r.due; cc += r.student_count;
            });
            rows.push({ data: [cls + " Total", "", cc, cm, cp, cd], style: classTotalStyle });
        });
        rows.push({ data: ["Grand Total", "", grand.count, grand.master, grand.paid, grand.due], style: grandTotalStyle });

        const wsData = [headers, ...rows.map((r) => r.data)];
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        headers.forEach((_, ci) => { const ref = XLSX.utils.encode_cell({ r: 0, c: ci }); if (ws[ref]) ws[ref].s = headerStyle; });
        rows.forEach((row, ri) => {
            const er = ri + 1;
            if (row.style) { for (let ci = 0; ci < 6; ci++) { const ref = XLSX.utils.encode_cell({ r: er, c: ci }); if (ws[ref]) ws[ref].s = row.style; } }
            for (let ci = 2; ci <= 5; ci++) { const ref = XLSX.utils.encode_cell({ r: er, c: ci }); if (ws[ref] && typeof ws[ref].v === "number") ws[ref].s = { ...(ws[ref].s || {}), ...numFmt }; }
        });
        ws["!cols"] = [{ wch: 20 }, { wch: 25 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
        return ws;
    };

    const handleExportExcel = () => {
        if (!hasData) { toast.error("No data"); return; }
        const wb = XLSX.utils.book_new();
        if (head1Data.length > 0) XLSX.utils.book_append_sheet(wb, buildExcelSheet(head1Data, "Head 1"), "Head 1");
        if (head2Data.length > 0) XLSX.utils.book_append_sheet(wb, buildExcelSheet(head2Data, "Head 2"), "Head 2");
        XLSX.writeFile(wb, `Due_Summary_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
    };

    // PDF: both head tables in one document
    const buildPdfSection = (doc, data, title, startY) => {
        const hierarchy = buildClassHierarchy(data);
        const grand = calcGrandTotal(data);
        const pdfHeaders = [["Class", title, "Lines", "Master Amount", "Paid Amount", "Due"]];
        const pdfRows = [];

        Object.keys(hierarchy).sort().forEach((cls) => {
            pdfRows.push({ data: [cls, "", "", "", "", ""], isClass: true });
            let cm = 0, cp = 0, cd = 0, cc = 0;
            hierarchy[cls].forEach((r) => {
                pdfRows.push({ data: ["", r.head, r.student_count, fmt(r.master_amount), fmt(r.paid_amount), fmt(r.due)] });
                cm += r.master_amount; cp += r.paid_amount; cd += r.due; cc += r.student_count;
            });
            pdfRows.push({ data: [cls + " Total", "", cc, fmt(cm), fmt(cp), fmt(cd)], isClassTotal: true });
        });
        pdfRows.push({ data: ["Grand Total", "", grand.count, fmt(grand.master), fmt(grand.paid), fmt(grand.due)], isGrand: true });

        autoTable(doc, {
            head: pdfHeaders,
            body: pdfRows.map((r) => r.data),
            startY,
            styles: { fontSize: 7.5, cellPadding: 2 },
            headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: "bold" },
            columnStyles: { 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
            didParseCell: (cellData) => {
                if (cellData.section !== "body") return;
                const row = pdfRows[cellData.row.index];
                if (!row) return;
                if (row.isClass) { cellData.cell.styles.fillColor = [187, 222, 251]; cellData.cell.styles.fontStyle = "bold"; }
                else if (row.isClassTotal) { cellData.cell.styles.fillColor = [232, 245, 233]; cellData.cell.styles.fontStyle = "bold"; }
                else if (row.isGrand) { cellData.cell.styles.fillColor = [255, 236, 179]; cellData.cell.styles.fontStyle = "bold"; }
            },
        });
        return doc.lastAutoTable.finalY;
    };

    const handleExportPdf = () => {
        if (!hasData) { toast.error("No data"); return; }
        const doc = new jsPDF("l", "mm", "a4");
        doc.setFontSize(14); doc.text("Due Summary", 14, 15);
        doc.setFontSize(9); doc.text(`Class: ${selectedClass.length > 0 ? selectedClass.join(", ") : "All"} | Type: ${selectedStdType || "All"} | Date: ${moment().format("DD MMM YYYY")}`, 14, 22);
        let y = 28;
        if (head1Data.length > 0) { doc.setFontSize(11); doc.text("Grouped by Head 1", 14, y); y = buildPdfSection(doc, head1Data, "Head 1", y + 3) + 8; }
        if (head2Data.length > 0) { if (y > 170) { doc.addPage(); y = 15; } doc.setFontSize(11); doc.text("Grouped by Head 2", 14, y); buildPdfSection(doc, head2Data, "Head 2", y + 3); }
        doc.save(`Due_Summary_${moment().format("YYYYMMDD_HHmm")}.pdf`);
    };

    return (
        <>
            <style>{tableStyles}</style>
            <Toaster />
            <div className="container-fluid mt-2 px-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <h5 className="mb-0 fw-bold">Due Summary</h5>
                    <div className="d-flex gap-1 flex-wrap">
                        <Link to="/due_summary_all" className="btn btn-outline-primary btn-sm px-2 py-0" style={{ fontSize: "0.75rem" }}>
                            <i className="fas fa-compress-arrows-alt me-1"></i>Without Class
                        </Link>
                        <Link to="/duelist" className="btn btn-outline-secondary btn-sm px-2 py-0" style={{ fontSize: "0.75rem" }}>
                            <i className="fas fa-arrow-left me-1"></i>Student Due
                        </Link>
                    </div>
                </div>

                {/* Filters */}
                <div className="row g-2 align-items-center">
                    <div className="col-md-2" style={{ position: "relative" }} ref={classDropdownRef}>
                        <div className="form-control form-control-sm d-flex align-items-center justify-content-between" style={{ cursor: "pointer", fontSize: "0.82rem" }} onClick={() => setShowClassDropdown((p) => !p)}>
                            <span className="text-truncate">{selectedClass.length === 0 ? "All Class" : selectedClass.length === stClass.length ? "All Class" : `${selectedClass.length} class`}</span>
                            <i className={`fas fa-chevron-${showClassDropdown ? "up" : "down"} ms-2`} style={{ fontSize: "0.65rem" }}></i>
                        </div>
                        {showClassDropdown && (
                            <div style={{ position: "absolute", top: "100%", left: "12px", right: "12px", zIndex: 1050, background: "#fff", border: "1px solid #dee2e6", borderRadius: "0 0 6px 6px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: "220px", overflowY: "auto" }}>
                                <div className="px-3 py-2 border-bottom" style={{ cursor: "pointer", background: "#f8f9fa", fontSize: "0.82rem" }} onClick={handleSelectAllClasses}>
                                    <input type="checkbox" checked={stClass.length > 0 && selectedClass.length === stClass.length} readOnly className="form-check-input me-2" style={{ marginTop: "2px" }} />
                                    <strong>Select All</strong>
                                </div>
                                {stClass.map((o, i) => (
                                    <div key={i} className="px-3 py-2" style={{ cursor: "pointer", fontSize: "0.82rem" }} onClick={() => handleToggleClass(o.class_name)} onMouseEnter={(e) => (e.currentTarget.style.background = "#e9ecef")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                                        <input type="checkbox" checked={selectedClass.includes(o.class_name)} readOnly className="form-check-input me-2" style={{ marginTop: "2px" }} />
                                        {o.class_name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="col-md-1">
                        <select onChange={(e) => setSelectedDev(e.target.value)} className="form-control form-control-sm">
                            <option value="">Div</option>
                            {stDev.map((o, i) => <option key={i} value={o.name}>{o.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-1">
                        <select onChange={handleSelectChangeYear} className="form-control form-control-sm border border-danger">
                            <option value="">Year</option>
                            {stYear.map((o, i) => <option key={i} value={o.name}>{o.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-2">
                        <input type="date" className="form-control form-control-sm" onChange={(e) => setDueDate(e.target.value)} title="Start Date" />
                    </div>
                    <div className="col-md-2">
                        <input type="date" className="form-control form-control-sm" onChange={(e) => setDueDateEnd(e.target.value)} title="End Date" />
                    </div>
                    <div className="col-md-1">
                        <select onChange={(e) => setSelectedStdType(e.target.value)} className="form-control form-control-sm">
                            <option value="">Type</option>
                            <option value="New">New</option>
                            <option value="Old">Old</option>
                        </select>
                    </div>
                    <div className="col-md-2" style={{ position: "relative" }} ref={feesDropdownRef}>
                        <div className="form-control form-control-sm d-flex align-items-center justify-content-between" style={{ cursor: "pointer", fontSize: "0.82rem" }} onClick={() => setShowFeesDropdown((p) => !p)}>
                            <span className="text-truncate">{selectedFeesType.length === 0 ? "Fees Type" : selectedFeesType.length === fees.length ? "All" : `${selectedFeesType.length} selected`}</span>
                            <i className={`fas fa-chevron-${showFeesDropdown ? "up" : "down"} ms-2`} style={{ fontSize: "0.65rem" }}></i>
                        </div>
                        {showFeesDropdown && (
                            <div style={{ position: "absolute", top: "100%", left: "12px", right: "12px", zIndex: 1050, background: "#fff", border: "1px solid #dee2e6", borderRadius: "0 0 6px 6px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: "220px", overflowY: "auto" }}>
                                <div className="px-3 py-2 border-bottom" style={{ cursor: "pointer", background: "#f8f9fa", fontSize: "0.82rem" }} onClick={handleSelectAllFeeTypes}>
                                    <input type="checkbox" checked={fees.length > 0 && selectedFeesType.length === fees.length} readOnly className="form-check-input me-2" style={{ marginTop: "2px" }} />
                                    <strong>Select All</strong>
                                </div>
                                {fees.map((option) => (
                                    <div key={option.id} className="px-3 py-2" style={{ cursor: "pointer", fontSize: "0.82rem" }} onClick={() => handleToggleFeeType(String(option.id))} onMouseEnter={(e) => (e.currentTarget.style.background = "#e9ecef")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                                        <input type="checkbox" checked={selectedFeesType.includes(String(option.id))} readOnly className="form-check-input me-2" style={{ marginTop: "2px" }} />
                                        {option.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="col-md-1">
                        <select onChange={(e) => setSelectedAgent(e.target.value)} className="form-control form-control-sm">
                            <option value="">Agent</option>
                            {stAgent.map((o, i) => <option key={i} value={o.name}>{o.name}</option>)}
                        </select>
                    </div>
                </div>

                <div className="row mt-2 mb-2 align-items-center">
                    <div className="col d-flex gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={handleClear}>Clear</button>
                        <button className="btn btn-sm btn-primary" onClick={fetchData}><i className="fas fa-search me-1"></i>Search</button>
                        <button className="btn btn-sm btn-success" onClick={handleExportExcel} disabled={!hasData}><i className="fas fa-file-excel me-1"></i>Excel</button>
                        <button className="btn btn-sm btn-danger" onClick={handleExportPdf} disabled={!hasData}><i className="fas fa-file-pdf me-1"></i>PDF</button>
                    </div>
                </div>

                <hr className="my-1" />

                {isLoading ? (
                    <TaskSkeleton />
                ) : !hasData ? (
                    <div className="alert alert-warning mt-2">Click Search to view summary.</div>
                ) : (
                    <>
                        {renderHeadTable(head1Data, "Head 1", "#1565C0")}
                        {renderHeadTable(head2Data, "Head 2", "#6A1B9A")}
                    </>
                )}
            </div>
        </>
    );
}
